import {
    AfterViewInit,
    Component,
    ElementRef,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    PLATFORM_ID,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Activity } from '../../core/dto/activity.dtos';

@Component({
    selector: 'app-products-list',
    templateUrl: './products-list.component.html',
    styleUrls: ['./products-list.component.scss'],
})
export class ProductsListComponent implements AfterViewInit, OnChanges, OnDestroy {
    @Input() products: Activity[] = [];

    /** Delay del autoplay en milisegundos (4 segundos por defecto) */
    @Input() autoplayDelay: number = 4000;

    /** Si false, fuerza desactivar autoplay aunque haya productos suficientes */
    @Input() autoplay: boolean = true;

    @ViewChild('swiperRef') swiperRef?: ElementRef<any>;

    /** Cantidad mínima de productos para activar flechas + autoplay */
    private readonly MIN_FOR_SCROLL = 4;

    /** True si el usuario tiene `prefers-reduced-motion: reduce` en el OS */
    private prefersReducedMotion = false;

    private swiperInitialized = false;

    /** Breakpoints Swiper: slides visibles por ancho de viewport */
    readonly breakpoints: Record<number, { slidesPerView: number; spaceBetween: number }> = {
        // 1.08 en mobile pequeño: peek del siguiente card → señal visual de "swipeable"
        320: { slidesPerView: 1.08, spaceBetween: 14 },
        576: { slidesPerView: 1.2, spaceBetween: 18 },
        768: { slidesPerView: 2, spaceBetween: 20 },
        992: { slidesPerView: 2.25, spaceBetween: 22 },
        1200: { slidesPerView: 3, spaceBetween: 24 },
        1500: { slidesPerView: 4, spaceBetween: 24 },
    };

    constructor(@Inject(PLATFORM_ID) private readonly platformId: object) {}

    ngAfterViewInit(): void {
        if (!isPlatformBrowser(this.platformId)) return;

        try {
            this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        } catch { /* ignore */ }

        // Si ya tenemos productos al montar, inicializar; si no, esperar a ngOnChanges
        this.tryInitWhenReady();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Cada cambio de products dispara init/re-init.
        // Esto cubre el caso típico: products llega async como [] → [N items] después del fetch.
        // Antes el guard `&& this.swiperInitialized` impedía que se inicializara la PRIMERA vez,
        // dejando el <swiper-container> con init="false" y los slides invisibles.
        if (changes['products']) {
            this.tryInitWhenReady();
        }
    }

    /**
     * Inicializa el swiper solo cuando:
     *  - estamos en browser (no SSR)
     *  - hay productos cargados
     *  - el <swiper-container> ya está en el DOM (el *ngIf en el template lo monta solo
     *    cuando products.length > 0, así que puede no existir aún el primer tick)
     *
     * Idempotente. Reintenta hasta 3 veces (≈48ms) por si Angular no terminó el render.
     */
    private tryInitWhenReady(retries = 3): void {
        if (!isPlatformBrowser(this.platformId)) return;
        if (!this.products || this.products.length === 0) return;

        Promise.resolve().then(() => {
            const el = this.swiperRef?.nativeElement;
            if (!el) {
                if (retries > 0) {
                    setTimeout(() => this.tryInitWhenReady(retries - 1), 16);
                }
                return;
            }
            this.initSwiper();
        });
    }

    ngOnDestroy(): void {
        const el = this.swiperRef?.nativeElement;
        try { el?.swiper?.destroy?.(true, true); } catch { /* ignore */ }
    }

    get showArrows(): boolean {
        return !!this.products && this.products.length >= this.MIN_FOR_SCROLL;
    }

    private get enableAutoplay(): boolean {
        return (
            this.autoplay &&
            !!this.products &&
            this.products.length >= this.MIN_FOR_SCROLL &&
            !this.prefersReducedMotion
        );
    }

    /**
     * Inicializa Swiper Element manualmente.
     * Esto garantiza que TODAS las opciones (autoplay, loop, breakpoints)
     * se apliquen antes del primer paint del slider, evitando el bug en que
     * los atributos seteados via Angular llegan tarde.
     */
    private initSwiper(): void {
        const el = this.swiperRef?.nativeElement;
        if (!el) return;

        // Si ya estaba inicializado, destruir primero para reaplicar params nuevos
        if (this.swiperInitialized && el.swiper?.destroy) {
            try { el.swiper.destroy(true, true); } catch { /* ignore */ }
        }

        const params: any = {
            slidesPerView: 1,
            spaceBetween: 24,
            speed: 650,
            grabCursor: true,
            // Centra los slides si hay menos cards que slidesPerView del breakpoint.
            // Sin esto, 3 cards en un viewport ≥1500px (slidesPerView=4) quedaban
            // pegados a la izquierda con espacio vacío a la derecha.
            centerInsufficientSlides: true,
            breakpoints: this.breakpoints,
        };

        if (this.enableAutoplay) {
            params.loop = true;
            params.autoplay = {
                delay: this.autoplayDelay,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
            };
        }

        Object.assign(el, params);
        try {
            el.initialize();
            this.swiperInitialized = true;
        } catch {
            // Fallback: ya estaba inicializado, intentar sólo actualizar params
            try {
                Object.assign(el.swiper?.params || {}, params);
                el.swiper?.update?.();
                if (this.enableAutoplay) el.swiper?.autoplay?.start?.();
            } catch { /* ignore */ }
        }
    }

    trackById(_: number, p: Activity): number | string {
        return p?.id ?? _;
    }

    prev(): void {
        this.swiperRef?.nativeElement?.swiper?.slidePrev();
    }

    next(): void {
        this.swiperRef?.nativeElement?.swiper?.slideNext();
    }
}
